import { useEffect } from 'react';
import { useWorkspaceStore } from '../store/workspaceStore';
import { setCollections, setSchemas } from '../store/collectionsStore';
import {
  setEnvironments,
  setSelectedEnvironment,
  setWorkspaceVariables,
  setGlobalVariables,
} from '../store/variablesStore';
import { loadWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { startWorkspaceFileWatcher } from '../services/workspaceFileWatcher';
import { scheduleExternalSnapshotApply } from '../services/gitFlush';
import { isGitWorkspace } from '../utils/workspaceStorage';
import { syncEngineEnabled } from '../utils/featureFlags';
import { dedupeCollectionRequestIds } from '../utils/localIds';
import { normalizeSchemas } from '../utils/schemaTree';

/**
 * Watches a git-storage workspace's folder on disk and reloads the in-memory
 * snapshot when it changes externally (or toasts when local edits made it
 * dirty). Pure Tauri (`invoke('start_workspace_watcher')`) — no-ops in browser.
 * Extracted verbatim from App (same effect / dep array / call position).
 *
 * @param {{ toastT: Function }} deps
 */
export function useWorkspaceFileWatcher({ toastT }) {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  useEffect(() => {
    if (!isGitWorkspace(currentWorkspace)) return undefined;
    let cancelled = false;
    let stopFn = null;

    startWorkspaceFileWatcher(currentWorkspace.rootPath, async ({ dirty }) => {
      if (dirty) {
        toastT('toast.workspaceExternalChange', 'warning');
        return;
      }
      try {
        // Phase 4 inbound path: for flag-enrolled workspaces SQLite is the
        // source of truth, so the external YAML change must be diff-applied
        // into it first (debounced; tombstones deleted files, emits outbox
        // ops). loadWorkspaceSnapshot below then reads the updated SQLite.
        if (syncEngineEnabled(currentWorkspace)) {
          await scheduleExternalSnapshotApply(currentWorkspace);
        }
        const snap = await loadWorkspaceSnapshot(currentWorkspace);
        setCollections(dedupeCollectionRequestIds(snap.collections || []));
        setSchemas(normalizeSchemas(snap.schemas));
        setGlobalVariables(snap.globalVariables || []);
        setWorkspaceVariables(snap.workspaceVariables || []);
        const envs = snap.environments || [];
        setEnvironments(envs);
        if (snap.selectedEnvironmentId) {
          const sel = envs.find((e) => String(e.id) === String(snap.selectedEnvironmentId));
          setSelectedEnvironment(sel || null);
        }
      } catch (err) {
        console.warn('[workspaceFileWatcher] reload failed:', err);
      }
    })
      .then((stop) => {
        stopFn = stop;
        // Cleanup already ran before start resolved — tear down immediately.
        if (cancelled) Promise.resolve(stop()).catch(() => {});
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      if (stopFn) Promise.resolve(stopFn()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rootPath + storage are what the watcher is bound to; the whole object changes on every store write, and each change would tear down and respawn the OS watcher
  }, [currentWorkspace?.rootPath, currentWorkspace?.storage, toastT]);
}
